import type { ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Building2, GaugeCircle, LogOut } from "lucide-react";

import { impersonation, staffToken } from "@/api/client";
import { ImpersonationBanner } from "./impersonation-banner";

/**
 * The staff console's frame.
 *
 * It carries the impersonation banner at the very top, above the chrome, because a banner
 * BELOW the navigation is a banner somebody scrolls past.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const impersonating = impersonation.isLive(impersonation.get());

  return (
    <div className={impersonating ? "pt-12" : undefined}>
      <ImpersonationBanner onExit={() => void navigate({ to: "/organizations" })} />

      <header className="border-b border-[var(--color-rule)] bg-[var(--color-panel)]">
        <div className="mx-auto flex max-w-7xl items-center gap-6 px-4 py-3">
          <span className="flex items-center gap-2 font-semibold">
            Tankly
            {/* The word STAFF is in the chrome, permanently. Two tabs, two rooms. */}
            <span className="rounded bg-[var(--color-alarm)] px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide text-[var(--color-alarm-ink)]">
              Staff
            </span>
          </span>

          <nav className="flex items-center gap-1 text-sm">
            <NavLink to="/organizations" icon={<Building2 aria-hidden className="size-4" />}>
              Organizations
            </NavLink>
            <NavLink to="/metrics" icon={<GaugeCircle aria-hidden className="size-4" />}>
              Platform
            </NavLink>
          </nav>

          <button
            type="button"
            onClick={() => {
              staffToken.set(null);
              impersonation.set(null);
              void navigate({ to: "/login" });
            }}
            className="ml-auto flex items-center gap-1.5 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
          >
            <LogOut aria-hidden className="size-4" />
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8">{children}</main>
    </div>
  );
}

function NavLink({ to, icon, children }: { to: string; icon: ReactNode; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-1.5 rounded px-3 py-1.5 text-[var(--color-muted)] hover:bg-[var(--color-panel-high)] hover:text-[var(--color-text)]"
      activeProps={{ className: "bg-[var(--color-panel-high)] text-[var(--color-text)]" }}
    >
      {icon}
      {children}
    </Link>
  );
}
